import React from 'react'
import { Form, Input, Select, Switch } from 'antd'
import { Box, Button, Card, Divider, Typography } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SearchIcon from '@mui/icons-material/Search'
import PaletteIcon from '@mui/icons-material/Palette'

import { ImageUrlInput } from './ImageUrlInput'
import { ColorPickerInput } from './ColorPickerInput'
import { GradientInput } from './GradientInput'

export interface SalepageMetaValues {
  seoTitle?: string
  seoDescription?: string
  seoKeywords?: string
  canonicalUrl?: string
  ogImage?: string
  ogTitle?: string
  ogDescription?: string
  twitterCard?: string
}

export interface SalepageThemeValues {
  fontFamily?: string
  fontUrl?: string
  fontColor?: string
  backgroundColor?: string
  backgroundImage?: string
  backgroundSize?: string
  backgroundPosition?: string
  backgroundRepeat?: string
  backgroundGradient?: string
}

interface SeoThemeTabProps {
  meta: SalepageMetaValues | undefined
  theme: SalepageThemeValues | undefined
  onSave: (meta: SalepageMetaValues, theme: SalepageThemeValues) => Promise<void>
  saving: boolean
  onPreview?: () => void
}

const TWITTER_CARD_OPTIONS = [
  { value: 'summary', label: 'summary' },
  { value: 'summary_large_image', label: 'summary_large_image' },
]

const BG_SIZE_OPTIONS = [
  { value: 'cover', label: 'cover' },
  { value: 'contain', label: 'contain' },
  { value: 'auto', label: 'auto' },
]

const BG_POSITION_OPTIONS = [
  { value: 'top left', label: 'top left' },
  { value: 'top center', label: 'top center' },
  { value: 'top right', label: 'top right' },
  { value: 'center left', label: 'center left' },
  { value: 'center center', label: 'center center' },
  { value: 'center right', label: 'center right' },
  { value: 'bottom left', label: 'bottom left' },
  { value: 'bottom center', label: 'bottom center' },
  { value: 'bottom right', label: 'bottom right' },
]

const BG_REPEAT_OPTIONS = [
  { value: 'no-repeat', label: 'no-repeat' },
  { value: 'repeat', label: 'repeat' },
]

const THAI_FONT_PRESETS = [
  { value: 'default', label: 'ระบบ (Default)', fontFamily: '', fontUrl: '' },
  { value: 'sarabun', label: 'Sarabun', fontFamily: 'Sarabun, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700&display=swap' },
  { value: 'prompt', label: 'Prompt', fontFamily: 'Prompt, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Prompt:wght@400;600;700&display=swap' },
  { value: 'kanit', label: 'Kanit', fontFamily: 'Kanit, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Kanit:wght@400;600;700&display=swap' },
  { value: 'bai-jamjuree', label: 'Bai Jamjuree', fontFamily: 'Bai Jamjuree, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Bai+Jamjuree:wght@400;600;700&display=swap' },
  { value: 'mitr', label: 'Mitr', fontFamily: 'Mitr, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Mitr:wght@400;600;700&display=swap' },
  { value: 'ibm-plex-sans-thai', label: 'IBM Plex Sans Thai', fontFamily: '"IBM Plex Sans Thai", sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@400;600;700&display=swap' },
  { value: 'athiti', label: 'Athiti', fontFamily: 'Athiti, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Athiti:wght@400;600;700&display=swap' },
  { value: 'chakra-petch', label: 'Chakra Petch', fontFamily: '"Chakra Petch", sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@400;600;700&display=swap' },
]

const SAMPLE_THAI_TEXT = 'ตัวอย่างข้อความภาษาไทย สวัสดีครับ สินค้าคุณภาพ ราคาพิเศษ'

export function SeoThemeTab({ meta, theme, onSave, saving, onPreview }: SeoThemeTabProps) {
  const [metaForm] = Form.useForm()
  const [themeForm] = Form.useForm()
  const fontUrl = Form.useWatch('fontUrl', themeForm)
  const fontFamily = Form.useWatch('fontFamily', themeForm)
  const fontColor = Form.useWatch('fontColor', themeForm)
  const backgroundColor = Form.useWatch('backgroundColor', themeForm)
  const backgroundImage = Form.useWatch('backgroundImage', themeForm)
  const backgroundSize = Form.useWatch('backgroundSize', themeForm)
  const backgroundPosition = Form.useWatch('backgroundPosition', themeForm)
  const backgroundRepeat = Form.useWatch('backgroundRepeat', themeForm)
  const backgroundGradientFormValue = Form.useWatch('backgroundGradient', themeForm)
  const [useGradient, setUseGradient] = React.useState(!!theme?.backgroundGradient)

  React.useEffect(() => {
    if (meta) metaForm.setFieldsValue(meta)
  }, [meta, metaForm])

  React.useEffect(() => {
    if (theme) {
      themeForm.setFieldsValue(theme)
      setUseGradient(!!theme.backgroundGradient)
    }
  }, [theme, themeForm])

  // ซิงค์ useGradient เมื่อกด ล้าง ใน GradientInput (onChange('') ส่ง '' มา)
  React.useEffect(() => {
    if (useGradient && backgroundGradientFormValue === '') {
      setUseGradient(false)
    }
  }, [useGradient, backgroundGradientFormValue])

  // โหลด Google Font เมื่อเลือก preset หรือกรอก URL
  React.useEffect(() => {
    if (!fontUrl || !fontUrl.startsWith('https://fonts.googleapis.com')) return
    const id = 'salepage-font-preview'
    let link = document.getElementById(id) as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = fontUrl
  }, [fontUrl])

  const handleFontPresetChange = (value: string | undefined) => {
    if (!value || value === 'default') {
      themeForm.setFieldsValue({ fontFamily: undefined, fontUrl: undefined })
      return
    }
    const preset = THAI_FONT_PRESETS.find((p) => p.value === value)
    if (preset) {
      themeForm.setFieldsValue({ fontFamily: preset.fontFamily, fontUrl: preset.fontUrl })
    }
  }

  const previewBgStyles = React.useMemo(() => {
    const bg: React.CSSProperties = {}
    if (backgroundGradientFormValue) {
      bg.background = backgroundGradientFormValue
    } else if (backgroundImage) {
      bg.backgroundImage = `url(${backgroundImage})`
      bg.backgroundSize = backgroundSize || 'cover'
      bg.backgroundPosition = backgroundPosition || 'center'
      bg.backgroundRepeat = (backgroundRepeat as React.CSSProperties['backgroundRepeat']) || 'no-repeat'
      if (backgroundColor) bg.backgroundColor = backgroundColor
    } else if (backgroundColor) {
      bg.backgroundColor = backgroundColor
    } else {
      bg.backgroundColor = '#f5f5f5'
    }
    return bg
  }, [backgroundColor, backgroundImage, backgroundSize, backgroundPosition, backgroundRepeat, backgroundGradientFormValue])

  const matchedPreset = React.useMemo(() => {
    if (!fontFamily && !fontUrl) return 'default'
    if (!fontFamily || !fontUrl) return undefined
    return THAI_FONT_PRESETS.find(
      (p) => p.value !== 'default' && (p.fontUrl === fontUrl || fontFamily?.includes(p.label)),
    )?.value
  }, [fontFamily, fontUrl])

  const handleSubmit = async () => {
    const metaValues = metaForm.getFieldsValue() as SalepageMetaValues
    const themeValues = themeForm.getFieldsValue() as SalepageThemeValues
    const cleanMeta = Object.fromEntries(
      Object.entries(metaValues ?? {}).filter(([, v]) => v != null && v !== ''),
    ) as SalepageMetaValues
    const cleanTheme = Object.fromEntries(
      Object.entries(themeValues ?? {}).filter(([, v]) => v != null && v !== ''),
    ) as SalepageThemeValues
    await onSave(cleanMeta, cleanTheme)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SearchIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            SEO Settings
          </Typography>
        </Box>
        <Form form={metaForm} layout="vertical" initialValues={meta}>
          <Form.Item name="seoTitle" label="SEO Title">
            <Input placeholder="ถ้าไม่ระบุจะใช้ชื่อหน้า" allowClear />
          </Form.Item>
          <Form.Item name="seoDescription" label="Meta Description">
            <Input.TextArea rows={2} placeholder="คำอธิบายสำหรับค้นหา" allowClear />
          </Form.Item>
          <Form.Item name="seoKeywords" label="Meta Keywords">
            <Input placeholder="คำค้นหา คั่นด้วย comma" allowClear />
          </Form.Item>
          <Form.Item name="canonicalUrl" label="Canonical URL">
            <Input placeholder="https://..." allowClear />
          </Form.Item>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Open Graph (แชร์บน Facebook, LINE)
          </Typography>
          <Form.Item name="ogTitle" label="OG Title">
            <Input placeholder="หัวข้อสำหรับแชร์" allowClear />
          </Form.Item>
          <Form.Item name="ogDescription" label="OG Description">
            <Input.TextArea rows={2} placeholder="คำอธิบายสำหรับแชร์" allowClear />
          </Form.Item>
          <Form.Item name="ogImage" label="OG Image">
            <ImageUrlInput placeholder="https://... หรืออัพโหลดรูปสำหรับแชร์" />
          </Form.Item>
          <Form.Item name="twitterCard" label="Twitter Card">
            <Select
              placeholder="เลือกประเภท"
              allowClear
              options={TWITTER_CARD_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PaletteIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Theme — Font & Background
          </Typography>
        </Box>
        <Form form={themeForm} layout="vertical" initialValues={theme}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Font
          </Typography>
          <Form.Item label="Preset ฟอนต์ไทย">
            <Select
              placeholder="เลือก preset ฟอนต์ภาษาไทย"
              allowClear
              value={matchedPreset}
              options={THAI_FONT_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
              onChange={handleFontPresetChange}
            />
          </Form.Item>
          <Form.Item name="fontFamily" label="Font Family">
            <Input placeholder="เช่น Sarabun, Prompt" allowClear />
          </Form.Item>
          <Form.Item name="fontUrl" label="Google Fonts URL">
            <Input
              placeholder="https://fonts.googleapis.com/css2?family=..."
              allowClear
            />
          </Form.Item>
          <Form.Item name="fontColor" label="Font Color">
            <ColorPickerInput placeholder="#333333 หรือ rgb(51,51,51)" />
          </Form.Item>
          {(fontFamily || fontUrl || fontColor || backgroundColor || backgroundImage || backgroundGradientFormValue) && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                ...previewBgStyles,
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                ตัวอย่างข้อความ
              </Typography>
              <Typography
                sx={{
                  fontFamily: fontFamily || 'inherit',
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  color: fontColor || 'inherit',
                }}
              >
                {SAMPLE_THAI_TEXT}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Background
          </Typography>
          <Form.Item name="backgroundColor" label="Background Color">
            <ColorPickerInput placeholder="#ffffff หรือ rgb(250,250,250)" />
          </Form.Item>
          <Form.Item name="backgroundImage" label="Background Image">
            <ImageUrlInput placeholder="https://... หรืออัพโหลดรูปพื้นหลัง" />
          </Form.Item>
          {backgroundImage && (
            <>
              <Form.Item name="backgroundSize" label="Background Size">
                <Select
                  placeholder="cover / contain / auto"
                  allowClear
                  options={BG_SIZE_OPTIONS}
                />
              </Form.Item>
              <Form.Item name="backgroundPosition" label="Background Position">
                <Select
                  placeholder="top left, top center, ..."
                  allowClear
                  options={BG_POSITION_OPTIONS}
                />
              </Form.Item>
              <Form.Item name="backgroundRepeat" label="Background Repeat">
                <Select
                  placeholder="no-repeat / repeat"
                  allowClear
                  options={BG_REPEAT_OPTIONS}
                />
              </Form.Item>
            </>
          )}
          <Form.Item label="Background Gradient">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (useGradient) {
                    setUseGradient(false)
                    themeForm.setFieldValue('backgroundGradient', undefined)
                  } else {
                    setUseGradient(true)
                    themeForm.setFieldValue('backgroundGradient', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    if (useGradient) {
                      setUseGradient(false)
                      themeForm.setFieldValue('backgroundGradient', undefined)
                    } else {
                      setUseGradient(true)
                      themeForm.setFieldValue('backgroundGradient', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                    }
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  width: 'fit-content',
                }}
              >
                <Box component="span" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={useGradient}
                    onChange={(checked) => {
                      setUseGradient(checked)
                      if (checked) {
                        themeForm.setFieldValue('backgroundGradient', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                      } else {
                        themeForm.setFieldValue('backgroundGradient', undefined)
                      }
                    }}
                  />
                </Box>
                <Typography variant="body2" component="span">
                  {useGradient ? 'กำหนด gradient' : 'ไม่กำหนด gradient'}
                </Typography>
              </Box>
              {useGradient ? (
                <Form.Item name="backgroundGradient" noStyle>
                  <GradientInput />
                </Form.Item>
              ) : null}
            </Box>
          </Form.Item>
        </Form>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="large"
          disabled={saving}
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
        >
          {saving ? 'กำลังบันทึก...' : 'บันทึก SEO & Theme'}
        </Button>
        {onPreview && (
          <Button
            variant="outlined"
            size="large"
            startIcon={<VisibilityIcon />}
            onClick={onPreview}
          >
            Preview
          </Button>
        )}
      </Box>
    </Box>
  )
}
